import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';

const SAFETY_GAP = 8;
const HYSTERESIS = 16;

const outerWidth = (node) => {
  if (!node) return 0;
  const cs = window.getComputedStyle(node);
  return node.getBoundingClientRect().width + (parseFloat(cs.marginLeft) || 0) + (parseFloat(cs.marginRight) || 0);
};

const horizontalPadding = (node) => {
  const cs = window.getComputedStyle(node);
  return (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
};

// Width the direct children of `col` need laid out in one line.
const contentWidth = (col) => {
  const kids = Array.from(col.children).filter((c) => c.offsetParent !== null);
  const gap = parseFloat(window.getComputedStyle(col).columnGap) || 0;
  return kids.reduce((sum, c) => sum + outerWidth(c), 0) + gap * Math.max(0, kids.length - 1);
};

export const AgentHeaderRow = ({ className = '', children }) => {
  const rowRef = useRef(null);
  const [fit, setFit] = useState({ versionCompact: false, settingsCompact: false, configureCompact: false });
  const fitRef = useRef(fit);

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === 'undefined') return undefined;
    const [leftCol, rightCol] = row.children;
    if (!leftCol || !rightCol) return undefined;

    const measure = () => {
      const q = (name) => row.querySelector(`[data-fit="${name}"]`);
      const versionShown = outerWidth(q('version-label'));
      const settingsShown = outerWidth(q('settings-label'));
      const configureShown = outerWidth(q('configure-label'));
      const leftWith = (versionW) => contentWidth(leftCol) - versionShown + versionW;
      const rightWith = (settingsW, configureW) =>
        contentWidth(rightCol) - settingsShown - configureShown + settingsW + configureW;

      const available = row.getBoundingClientRect().width - horizontalPadding(row)
        - horizontalPadding(leftCol) - horizontalPadding(rightCol) - SAFETY_GAP;
      const fits = (left, right, isCompactNow) => left + right + (isCompactNow ? HYSTERESIS : 0) <= available;

      const versionFull = outerWidth(q('version-full'));
      const versionShort = outerWidth(q('version-short'));
      const settingsFull = outerWidth(q('settings-full'));
      const configureFull = outerWidth(q('configure-full'));

      const prev = fitRef.current;
      const next = {
        versionCompact: !fits(leftWith(versionFull), rightWith(settingsFull, configureFull), prev.versionCompact),
        settingsCompact: !fits(leftWith(versionShort), rightWith(settingsFull, configureFull), prev.settingsCompact),
        configureCompact: !fits(leftWith(versionShort), rightWith(0, configureFull), prev.configureCompact),
      };
      if (Object.keys(next).every((k) => prev[k] === next[k])) return;
      fitRef.current = next;
      setFit(next);
    };

    let frame = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    // The row resizes with the window / sidebar; the items inside resize when
    // e.g. the status badge changes (RUNNING -> COMPLETED).
    ro.observe(row);
    [leftCol, rightCol].forEach((col) => Array.from(col.children).forEach((c) => ro.observe(c)));
    measure();
    return () => {
      ro.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div ref={rowRef} className={className}>
      {children(fit)}
    </div>
  );
};

const VersionSelector = ({ versions, selectedVersionNumber, onShow, onRestore, onCompare, restoreDisabled, canRestore = false, disabled = false, disabledTooltip, compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('single');                // 'single' | 'compare'
  const [selectedForCompare, setSelectedForCompare] = useState([]); // up to 2 version numbers
  const containerRef = useRef(null);

  const latestVersionNumber = versions?.[0]?.version;
  const shownVersion = selectedVersionNumber === 0 ? 0 : (selectedVersionNumber || latestVersionNumber);
  const buttonLabel = shownVersion === 0 ? 'Mapped Response' : `Agent Version ${shownVersion}`;
  const buttonShortLabel = shownVersion === 0 ? 'Mapped' : `V${shownVersion}`;

  const resetCompareState = () => {
    setMode('single');
    setSelectedForCompare([]);
  };
  // Close on click outside. Only while open — one instance renders per agent,
  // so an unconditional document listener meant hundreds of handlers per template.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        resetCompareState();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on versions refresh (template reload)
  useEffect(() => {
    setIsOpen(false);
    resetCompareState();
  }, [versions]);

  if (!versions || versions.length === 0) {
    return null;
  }

  const canCompare = versions.length >= 2 && typeof onCompare === 'function';

  // Checkbox helpers
  const isChecked = (versionNum) => selectedForCompare.includes(versionNum);
  const toggleSelectForCompare = (versionNum) => {
    setSelectedForCompare(prev => {
      if (prev.includes(versionNum)) {
        return prev.filter(v => v !== versionNum);
      }
      if (prev.length >= 2) return prev;
      return [...prev, versionNum];
    });
  };

  const handleCompareClick = () => {
    if (selectedForCompare.length !== 2) return;
    const [a, b] = selectedForCompare;
    const versionA = Math.min(a, b);
    const versionB = Math.max(a, b);
    onCompare(versionA, versionB);
    setIsOpen(false);
    resetCompareState();
  };

  const switchMode = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    setSelectedForCompare([]);
  };

  return (
    <div ref={containerRef} className={`d-inline-block position-relative ms-2${disabled && disabledTooltip ? ' avatar-tooltip' : ''}`} data-tooltip={disabled ? disabledTooltip : undefined}>
      <button
        type="button"
        className={`formField4 d-flex align-items-center gap-2 text-start p-1 feedback-field ev-reg-p-blue ev-radius-xsmall px-2 ms-1${compact && !disabled ? ' avatar-tooltip' : ''}`}
        data-tooltip={compact && !disabled ? buttonLabel : undefined}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          const next = !isOpen;
          setIsOpen(next);
          if (!next) resetCompareState();
        }}
      >
        {/* `compact` comes from AgentHeaderRow, which measures the agent header.
            The hidden copies give it both label widths whichever one is shown. */}
        <span data-fit="version-label" className="ev-bold-reg">{compact ? buttonShortLabel : buttonLabel}</span>
        <span data-fit="version-full" className="ev-bold-reg position-absolute invisible text-nowrap pe-none" aria-hidden="true">
          {buttonLabel}
        </span>
        <span data-fit="version-short" className="ev-bold-reg position-absolute invisible text-nowrap pe-none" aria-hidden="true">
          {buttonShortLabel}
        </span>
        <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
      </button>

      {isOpen && (
        <div className="ev-card-4 position-absolute top-100 mt-2 p-2 z-1000" style={{ minWidth: '380px' }}>

          {/* ── Mode toggle: Single | Compare ── */}
          {canCompare && (
            <div className="d-flex justify-content-center mb-2">
              <div className="btn-group custom-btn-group-outer" role="group">
                <button
                  type="button"
                  className={`btn genbi-custom-btn-group-inner ${mode === 'single' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                  onClick={() => switchMode('single')}
                >
                  Single
                </button>
                <button
                  type="button"
                  className={`btn genbi-custom-btn-group-inner ${mode === 'compare' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                  onClick={() => switchMode('compare')}
                >
                  Compare
                </button>
              </div>
            </div>
          )}

          <div className="scrollable-element vh-20 p-1">
            {versions.map(v => {
              const isMappedVersion = v.version === 0;
              const versionName = isMappedVersion
                ? 'Mapped Response'
                : `Agent Version ${v.version}`;
              const checked = isChecked(v.version);
              const disableCheckbox =
                mode === 'compare' && !checked && selectedForCompare.length >= 2;

              return (
                <div
                  key={v.version}
                  className={`ev-card-7 d-flex justify-content-between align-items-center mb-2 p-2 ev-radius-xsmall${isMappedVersion ? ' mapped-version-card' : ''}`}
                >
                  <div
                    className={`d-flex align-items-center`}
                    onClick={mode === 'compare' && !disableCheckbox ? () => toggleSelectForCompare(v.version) : undefined}
                  >
                    {mode === 'compare' && (
                      <input
                        type="checkbox"
                        className="form-check-input version-compare-checkbox me-2"
                        checked={checked}
                        disabled={disableCheckbox}
                        onChange={() => toggleSelectForCompare(v.version)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select ${versionName} for comparison`}
                      />
                    )}
                    <span className="ev-small-p-dark me-2">{versionName}</span>
                    {isMappedVersion && (
                      <span className="genbi-source-mapped">
                        <i className="bi bi-file-earmark-text me-1"></i>Original
                      </span>
                    )}
                    {v.source && (
                      <span className={`ev-btn-xsmall ${v.source === 'manual' ? 'genbi-source-manual' : 'genbi-source-ai'}`}>
                        {v.source === 'manual' ? (
                          <><i className="bi bi-person-fill me-1 ev-bold-reg"></i>Manual</>
                        ) : v.source === 'formatting' ? (
                          <><i className="bi bi-spellcheck me-1 ev-bold-reg"></i>Formatting</>
                        ) : (
                          <><i className="bi bi-stars ev-bold-reg"></i>AI</>
                        )}
                      </span>
                    )}
                  </div>

                  {/* Action buttons — only in single mode. Compare mode uses checkbox + footer. */}
                  {mode === 'single' && (
                    <div>
                      {canRestore && (
                        <button
                          type="button"
                          className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg avatar-tooltip"
                          disabled={restoreDisabled}
                          data-tooltip={restoreDisabled ? 'Wait for agents to complete' : undefined}
                          onClick={() => {
                            onRestore(v.version, versionName);
                            setIsOpen(false);
                            resetCompareState();
                          }}
                        >
                          Restore Version
                        </button>
                      )}
                      <button
                        type="button"
                        className="ev-btn-blue-fill-round ev-btn-xsmall ev-bold-reg ms-1"
                        onClick={() => {
                          onShow(v.version, versionName, v.source);
                          setIsOpen(false);
                          resetCompareState();
                        }}
                      >
                        Show
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Compare footer (only in compare mode) ── */}
          {mode === 'compare' && (
            <div className="version-compare-footer d-flex justify-content-end align-items-center pt-2 mt-1 border-top">
              <span className="ev-xsmall-p-dark me-2" style={{ opacity: 0.7 }}>
                {selectedForCompare.length}/2 selected
              </span>
              <button
                type="button"
                className="ev-btn-blue-fill-round ev-btn-xsmall ev-bold-reg"
                onClick={handleCompareClick}
                disabled={selectedForCompare.length !== 2}
              >
                <i className="bi bi-files me-1"></i>
                Compare
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VersionSelector;